import * as React from 'react';
import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import { iataWithAdditionalQueryForServices, useAdditionalQueryForServices, useServicePageProps } from '../hooks';
import { useUpsaleWarning } from '../../../Checkout/components/Checkout/Upsale/hooks';
import { ServiceType } from '../../../Checkout/components/Checkout/OrderPaidServiceCards/types';
import { useSelector } from 'react-redux';
import {
	getAnimalServices,
	getBaggageMinPrice,
	getBaggageServices,
	getBrandIncludedBaggage,
	getServicesFallbackURL,
	shouldRedirectToFallback
} from '../../../Checkout/store/order/selectors';
import Warning from '../../../Checkout/components/Checkout/Upsale/Warning/Warning';
import ServicePage from '../../../Checkout/components/Checkout/ServicePage/ServicePage';
import ServiceCardDialog from '../../../Checkout/components/Checkout/OrderPaidServiceCards/components/ServiceCardDialog/ServiceCardDialog';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme';
import icons from '../../icons';
import { useRenderers } from '../../../renderProps';
import { ServiceButton, AdditionalServiceType } from '../../types';
import { useConfig } from '../../../context';
import { Money } from '../../../types';
import cn from 'classnames';

import BaggageSummary from '../../../ServiceSummary/services/BaggageSummary';
import { useCart } from '../../../Checkout/components/Checkout/Stepbar/hooks';
import { useIsCanModifyServices } from '../../hooks';
import BaggageContextProvider from '../../../Baggage/context';
import { useCartPrices } from '../../../Cart/hooks';
import SimpleLoader from '../../../SimpleLoader';
import { useIataCode } from '../../../hooks';

interface Props {
	aggregationMode?: boolean;
	segmentId?: string;
	isSelected?: boolean;
	minPrice?: Money;
	totalPrice?: Money;
	aggregationIcons?: React.ReactNode[];
	className?: string;
}

const BaggageAdditionalService: React.FC<Props> = ({
	aggregationMode,
	minPrice: minPriceProps,
	totalPrice: totalPriceProps,
	isSelected: isSelectedProps,
	segmentId,
	children,
	aggregationIcons,
	className
}) => {
	const { t } = useTranslation('Checkout');
	const minPrice = aggregationMode ? minPriceProps : useSelector(getBaggageMinPrice);
	const { CheckoutAdditionalServiceBaggage: css } = useTheme('CheckoutAdditionalService');

	const { servicesAvailable } = useConfig().global;
	const iataCode = useIataCode();

	const baggageServices = useSelector(getBaggageServices);
	const includedBrandServices = useSelector(getBrandIncludedBaggage);
	const servicePageProps = useServicePageProps(OrderAdditionalServiceGdsServiceServiceType.Baggage);
	const servicesRedirectURL = useSelector(getServicesFallbackURL);
	const shouldRedirect = useSelector(shouldRedirectToFallback);
	const { showWarning, closeWarning, toggleWarningIfOrderConfirmed } = useUpsaleWarning();
	const handleClick = shouldRedirect
		? toggleWarningIfOrderConfirmed
		: servicePageProps.toggleModal.bind(null, ServiceType.Baggage);

	const animalServices = useSelector(getAnimalServices);
	const baggageTotal = useCartPrices().baggage;

	const { onDeleteItem } = useCart();

	const isCanModifyServices = useIsCanModifyServices(baggageServices);

	const {
		isLoading: isAdditionalServicesLoading,
		loadAndDispatch: loadAndDispatchAdditionalServices
	} = useAdditionalQueryForServices();

	const buttons: ServiceButton[] = [
		{
			text: isCanModifyServices ? t('Change') : t('More details'),
			action: () => {
				if (iataWithAdditionalQueryForServices.includes(iataCode)) {
					loadAndDispatchAdditionalServices();
				}
				servicePageProps.toggleModal(ServiceType.Baggage);
			},
			buttonType: 'change'
		}
	];

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	if (
		!!servicesAvailable?.animal &&
		!includedBrandServices.length &&
		baggageServices.length === animalServices.length
	) {
		return null;
	}

	return (
		<>
			<BaggageSummary
				segmentId={segmentId}
				onServiceDelete={onDeleteItem}
			>
				{content => (
					<ServiceWrapper
						addClick={showWarning ? toggleWarningIfOrderConfirmed : handleClick}
						buttons={buttons}
						icon={icons.baggageIcon}
						className={cn(css.baggage, className)}
						coverClassName={css.baggage_aggregation}
						mode={aggregationMode ? 'aggregator' : 'default'}
						priceFrom={minPrice}
						isSelected={!!content}
						header={t('Baggage')}
						description={t('Transportation of impressions no limitations')}
						priceTotal={baggageTotal}
						aggregationIcons={aggregationIcons}
						serviceType={AdditionalServiceType.Baggage}
						dataTestId="baggage-service-card"
						addButtonText={!isCanModifyServices ? t('More details') : null}
					>
						{content}
					</ServiceWrapper>
				)}
			</BaggageSummary>

			<Warning open={showWarning} redirectURL={servicesRedirectURL} onClose={closeWarning} />
			{isAdditionalServicesLoading && <SimpleLoader />}

			<ServicePage>
				{childrenProps => (
					<BaggageContextProvider value={{ isReadOnly: !isCanModifyServices }}>
						<ServiceCardDialog
							order={servicePageProps.order}
							openedModal={isAdditionalServicesLoading ? null : servicePageProps.openedModal}
							onClose={servicePageProps.toggleModal.bind(null, null)}
							passengers={servicePageProps.passengers}
							passengerLabels={servicePageProps.passengerLabels}
							analyticsMeta={servicePageProps.analyticsMeta}
							{...childrenProps}
							baggageTotalPrice={baggageTotal}
						/>
					</BaggageContextProvider>
				)}
			</ServicePage>
		</>
	);
};

export default BaggageAdditionalService;
